import { useState, useEffect, useRef } from 'react';
import { useModalStore } from '../../../stores/modalStore.js';
import { useServerStore } from '../../../stores/serverStore.js';
import { useRoleStore } from '../../../stores/roleStore.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

const PERMISSIONS = [
  { key: 'ADMINISTRATOR',     label: 'Administrator',       desc: 'Grants all permissions (use with care)' },
  { key: 'MANAGE_SERVER',     label: 'Manage Server',       desc: 'Edit server settings and features' },
  { key: 'MANAGE_CHANNELS',   label: 'Manage Channels',     desc: 'Create, edit, and delete channels' },
  { key: 'MANAGE_ROLES',      label: 'Manage Roles',        desc: 'Create and assign roles below their own' },
  { key: 'MANAGE_MESSAGES',   label: 'Manage Messages',     desc: 'Delete and pin messages' },
  { key: 'KICK_MEMBERS',      label: 'Kick Members',        desc: 'Remove members from this server' },
  { key: 'BAN_MEMBERS',       label: 'Ban Members',         desc: 'Permanently ban members' },
  { key: 'MUTE_MEMBERS',      label: 'Mute Members',        desc: 'Mute members in voice channels' },
  { key: 'SEND_MESSAGES',     label: 'Send Messages',       desc: 'Post messages in text channels' },
  { key: 'READ_MESSAGES',     label: 'Read Messages',       desc: 'View message history in channels' },
  { key: 'ATTACH_FILES',      label: 'Attach Files',        desc: 'Upload files and images' },
  { key: 'ADD_REACTIONS',     label: 'Add Reactions',       desc: 'React to messages with emoji' },
  { key: 'MENTION_EVERYONE',  label: 'Mention @everyone',   desc: 'Ping all members of the server' },
  { key: 'CONNECT',           label: 'Connect',             desc: 'Join voice and video channels' },
  { key: 'SPEAK',             label: 'Speak',               desc: 'Transmit audio in voice channels' },
  { key: 'USE_VAD',           label: 'Use Voice Activity',  desc: 'Speak without push-to-talk' },
];

const PRESET_COLORS = ['#f59e0b','#ef4444','#10b981','#6366f1','#0ea5e9','#ec4899','#8b5cf6','#f97316','#14b8a6','#6b7280'];

export function RoleEditorModal() {
  const { isOpen, type, data, onClose, onOpen } = useModalStore();
  const currentServerId = useServerStore(s => s.currentServerId);
  const { getServerRoles, fetchRoles, createRole, updateRole, deleteRole } = useRoleStore();

  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [tab, setTab] = useState('display');        // display | permissions
  const [form, setForm] = useState({ name: '', color: '#6b7280', hoist: false, mentionable: false, permissions: {} });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [msg, setMsg] = useState(null);

  const serverId = data?.serverId || currentServerId;
  const roles = getServerRoles(serverId);
  const editableRoles = roles.filter(r => !r.isEveryone);
  const selectedRole = roles.find(r => r.id === selectedRoleId);

  const ref = useRef();
  useFocusTrap(ref, isOpen && type === 'roleEditor');

  useEffect(() => {
    if (isOpen && type === 'roleEditor' && serverId) {
      fetchRoles(serverId);
    }
  }, [isOpen, type, serverId]);

  useEffect(() => {
    if (selectedRole) {
      setForm({
        name: selectedRole.name,
        color: selectedRole.color || '#6b7280',
        hoist: !!selectedRole.hoist,
        mentionable: !!selectedRole.mentionable,
        permissions: { ...(selectedRole.permissions || {}) },
      });
      setConfirmDelete(false);
      setMsg(null);
    }
  }, [selectedRoleId]);

  useEffect(() => {
    if (editableRoles.length && !selectedRoleId) {
      setSelectedRoleId(editableRoles[0].id);
    }
  }, [roles.length]);

  if (!isOpen || type !== 'roleEditor') return null;

  const setPermission = (key, val) => {
    setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: val } }));
  };

  const handleSave = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      await updateRole(serverId, selectedRoleId, form);
      setMsg({ ok: true, text: 'Role saved.' });
    } catch {
      setMsg({ ok: false, text: 'Failed to save.' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 2500);
    }
  };

  const handleCreate = async () => {
    const role = await createRole(serverId, { name: 'New Role', color: '#6b7280', permissions: {} });
    if (role) setSelectedRoleId(role.id);
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    await deleteRole(serverId, selectedRoleId);
    setSelectedRoleId(editableRoles.find(r => r.id !== selectedRoleId)?.id || null);
    setConfirmDelete(false);
  };

  return (
    <div
      className="modal-overlay"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Role Editor"
        onClick={e => e.stopPropagation()}
        style={{ background: '#16213e', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 14, width: 720, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.7)' }}
      >
        {/* Left: role list */}
        <div style={{ width: 200, borderRight: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', background: '#12192e', flexShrink: 0 }}>
          <div style={{ padding: '18px 14px 10px', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Roles</div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {editableRoles.map(role => (
              <button
                key={role.id}
                onClick={() => setSelectedRoleId(role.id)}
                style={{
                  width: '100%', textAlign: 'left', padding: '9px 14px',
                  background: selectedRoleId === role.id ? 'rgba(255,255,255,0.07)' : 'none',
                  border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  color: selectedRoleId === role.id ? '#fff' : 'rgba(255,255,255,0.55)', fontSize: 13,
                }}
              >
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: role.color || '#6b7280', flexShrink: 0, display: 'inline-block' }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{role.name}</span>
              </button>
            ))}
          </div>
          <div style={{ padding: 10 }}>
            <button
              onClick={handleCreate}
              style={{ width: '100%', padding: '8px', borderRadius: 7, background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)', color: '#a5b4fc', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              + Create Role
            </button>
          </div>
        </div>

        {/* Right: editor */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Header */}
          <div style={{ padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>Edit Role</div>
              {selectedRole && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{selectedRole.name}</div>}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, padding: '0 22px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
            {['display', 'permissions'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: '11px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  color: tab === t ? '#a5b4fc' : 'rgba(255,255,255,0.4)',
                  borderBottom: `2px solid ${tab === t ? '#6366f1' : 'transparent'}`, marginBottom: -1,
                }}
              >
                {t === 'display' ? 'Display' : 'Permissions'}
              </button>
            ))}
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
            {!selectedRole ? (
              <div style={{ textAlign: 'center', paddingTop: 60, color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>Select or create a role</div>
            ) : tab === 'display' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Name */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 7 }}>Role Name</label>
                  <input
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 7, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                    maxLength={100}
                  />
                </div>

                {/* Color */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 7 }}>Role Color</label>
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                    {PRESET_COLORS.map(c => (
                      <button
                        key={c}
                        onClick={() => setForm(f => ({ ...f, color: c }))}
                        style={{ width: 28, height: 28, borderRadius: '50%', background: c, border: form.color === c ? '3px solid #fff' : '2px solid transparent', cursor: 'pointer', outline: 'none' }}
                        title={c}
                      />
                    ))}
                    <input
                      type="color"
                      value={form.color}
                      onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                      style={{ width: 36, height: 28, borderRadius: 5, border: '2px solid rgba(255,255,255,0.15)', background: 'none', cursor: 'pointer', padding: 2 }}
                      title="Custom color"
                    />
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>{form.color}</span>
                  </div>
                </div>

                {/* Toggles */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[
                    { key: 'hoist', label: 'Display separately', desc: 'Show this role in the members list as its own group' },
                    { key: 'mentionable', label: 'Allow @mention', desc: 'Allow anyone to mention all members with this role' },
                  ].map(({ key, label, desc }) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 14, color: '#e2e8f0', fontWeight: 600 }}>{label}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', marginTop: 2 }}>{desc}</div>
                      </div>
                      <div
                        onClick={() => setForm(f => ({ ...f, [key]: !f[key] }))}
                        style={{
                          width: 40, height: 22, borderRadius: 11, cursor: 'pointer', flexShrink: 0,
                          background: form[key] ? '#6366f1' : 'rgba(255,255,255,0.12)',
                          transition: 'background 0.2s', position: 'relative',
                        }}
                      >
                        <div style={{
                          width: 16, height: 16, borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: 3, left: form[key] ? 21 : 3, transition: 'left 0.2s',
                        }} />
                      </div>
                    </label>
                  ))}
                </div>

                {/* Preview */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 7 }}>Preview</label>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: form.color, display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: form.color }}>{form.name || 'Role Name'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {PERMISSIONS.map(({ key, label, desc }) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{desc}</div>
                    </div>
                    <div
                      onClick={() => setPermission(key, !form.permissions[key])}
                      style={{
                        width: 40, height: 22, borderRadius: 11, cursor: 'pointer', flexShrink: 0,
                        background: form.permissions[key] ? '#6366f1' : 'rgba(255,255,255,0.12)',
                        transition: 'background 0.2s', position: 'relative',
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 3, left: form.permissions[key] ? 21 : 3, transition: 'left 0.2s',
                      }} />
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '14px 22px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {selectedRole && !selectedRole.isEveryone && (
                <button
                  onClick={handleDelete}
                  style={{ padding: '7px 14px', borderRadius: 7, background: confirmDelete ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.1)', border: `1px solid rgba(239,68,68,${confirmDelete ? '0.6' : '0.3'})`, color: '#f87171', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  {confirmDelete ? 'Confirm Delete' : '🗑 Delete Role'}
                </button>
              )}
              {msg && <span style={{ fontSize: 12, color: msg.ok ? '#34d399' : '#f87171' }}>{msg.text}</span>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 7, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving || !selectedRole}
                style={{ padding: '7px 16px', borderRadius: 7, background: saving ? 'rgba(99,102,241,0.5)' : '#6366f1', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
